import { capitalize, filter, find, includes, isEmpty, map, size } from "lodash";
import * as React from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useCheckEventAccess } from "src/components/Event/useCheckEventAccess";
import { useGetEventDetailQuery } from "src/queries/useGetEventDetailQuery";
import { formatTitleHeader } from "src/utils/formatUtils";
import { BackButton } from "@ic-anywhere/ic-components";
import { DD_MMM_YYYY, formatDate, isDatesEqual } from "src/utils/dateUtils";
import { getReorderedSlotsByDateWithTimeZone } from "src/components/Event/Corporate/Tabs/SlotsDetail/SlotsDetailBody.utils";
import { Timeline, TimelineDataItem } from "./Timeline";
import { Slot } from "src/containers/event/event.types";
import { SlotCard } from "./SlotCard/SlotCard";
import { RibbonBar } from "./RibbonBar";
import { SlotSelectionSummary } from "./SlotSelectionSummary";
import { Loader } from "@access-selfcare/shared-libs";
import { useAtomValue } from "jotai";
import { userProfileStateAtom } from "src/state/userProfileAtoms";
import { useUpdateBookingsMutation } from "src/queries/invitationMutations";
import { buildUrlV2, ROUTES } from "src/v2/routes";
import { mapBookingUpdate } from "./SlotSelection.utils";

const SlotSelection: React.FC = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const userProfile = useAtomValue(userProfileStateAtom);
  const navigate = useNavigate();
  const userId = userProfile.userInfo.userId || "";
  const accessCheck = useCheckEventAccess(eventId);
  const { data: eventDetail, isLoading } = useGetEventDetailQuery(eventId, accessCheck === "access");
  const { mutate: updateBookings, isLoading: isUpdating } = useUpdateBookingsMutation(eventId, {
    onSuccess: () => {
      navigate(buildUrlV2(ROUTES.EVENT_DETAIL, { id: eventId }));
    },
  });
  const reorderSlotsGroupByDate = getReorderedSlotsByDateWithTimeZone(eventDetail?.slots);
  const hasDifferentDate = !isDatesEqual(eventDetail?.event.startDate, eventDetail?.event.endDate);
  const [expandedSlots, setExpandedSlots] = React.useState<string[]>([]);
  const [selectedSlots, setSelectedSlots] = React.useState<Slot[]>([]);

  const handleToggleExpand = (slotId: string): void => {
    const slots: string[] = includes(expandedSlots, slotId)
      ? filter(expandedSlots, (s) => s !== slotId)
      : [...expandedSlots, slotId];

    setExpandedSlots(slots);
  };
  const handleSlotSelect = (slot: Slot): void => {
    const isSelected = !!find(selectedSlots, ({ id }) => id === slot.id);
    const slots: Slot[] = isSelected ? filter(selectedSlots, (s) => s.id !== slot.id) : [...selectedSlots, slot];
    setSelectedSlots(slots);
  };
  const handleConfirm = (): void => {
    const payload = map(selectedSlots, (slot) => {
      const bookingToUpdate = find(slot.bookings, (booking) => booking.inviteeContactId === userId);
      return mapBookingUpdate(bookingToUpdate, slot, userId);
    });
    if (!isEmpty(payload)) {
      updateBookings(payload);
    }
  };

  const handleOnCancel = (): void => {
    navigate(buildUrlV2(ROUTES.EVENT_DETAIL, { id: eventId }));
  };

  return (
    <div className="container-xxl mt-4">
      <div className="d-flex flex-column">
        <BackButton className="align-self-start" />
        <Loader containerClass={"d-flex justify-content-center"} isVisible={isLoading} />
        {eventDetail && (
          <>
            <span className="mt-4 text-secondary">{formatTitleHeader(capitalize(eventDetail.event.category))}</span>
            <h1 className="mt-3">{formatTitleHeader(capitalize(eventDetail.event.name))}</h1>
            <span className="mt-3 mb-3 text-secondary">
              {hasDifferentDate
                ? `${formatDate(eventDetail.event.startDate, DD_MMM_YYYY)} - ${formatDate(
                    eventDetail.event.endDate,
                    DD_MMM_YYYY
                  )}`
                : formatDate(eventDetail.event.startDate, DD_MMM_YYYY)}
            </span>
            <div className="d-flex flex-row gap-4">
              <Timeline items={reorderSlotsGroupByDate}>
                {(item: TimelineDataItem): React.ReactNode =>
                  map(item.slots, (slot: Slot, slotIndex) => (
                    <SlotCard
                      key={slot.id}
                      slot={slot}
                      isExpanded={includes(expandedSlots, slot.id)}
                      isSelected={!!find(selectedSlots, ({ id }) => id === slot.id)}
                      onSelect={handleSlotSelect}
                      onToggleExpand={handleToggleExpand}
                      checkboxId={`checkbox-${slotIndex}`}
                    />
                  ))
                }
              </Timeline>
              <div
                className={`d-flex flex-column bg-lvl2 col-4 ${
                  isEmpty(selectedSlots) ? "p-4 align-items-center" : "px-4 py-3"
                }`}
              >
                {isEmpty(selectedSlots) ? (
                  <div className="d-flex flex-column align-items-center gap-2 mt-5 col-10">
                    <span className="icon icon-xl icon-outlined fw-medium text-secondary">info</span>
                    <span className="fs-24 fw-medium text-secondary text-center">No session selected yet</span>
                    <span className="text-secondary text-center">
                      Start browsing the different session programmed for this event
                    </span>
                  </div>
                ) : (
                  <SlotSelectionSummary slots={selectedSlots} onRemove={handleSlotSelect} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <RibbonBar
        selectedCount={size(selectedSlots)}
        onCancel={handleOnCancel}
        onConfirm={handleConfirm}
        confirmDisabled={isEmpty(selectedSlots) || isUpdating}
      />
    </div>
  );
};

export default SlotSelection;
